import type { AppLanguage } from "~/i18n/languagePreference";

import { activeLanguage, localeTagFor } from "~/helpers/activeLocale";

const MAX_COMPACT_INTEGER_DIGITS = 3;

const NARROW_SYMBOL_CURRENCY = "USD";

export const NARROW_CURRENCY_SYMBOL: Intl.NumberFormatOptions = { currencyDisplay: "narrowSymbol" };

export const narrowCurrencySymbolFor = (currency: string): Intl.NumberFormatOptions =>
	currency === NARROW_SYMBOL_CURRENCY ? NARROW_CURRENCY_SYMBOL : {};

const formatterCache = new Map<string, Intl.NumberFormat>();

const localeNumberFormatter = (language: AppLanguage, options?: Intl.NumberFormatOptions): Intl.NumberFormat => {
	const cacheKey = `${language}|${JSON.stringify(options ?? {})}`;
	const cached = formatterCache.get(cacheKey);
	if (cached) return cached;
	const formatter = new Intl.NumberFormat(localeTagFor(language), options);
	formatterCache.set(cacheKey, formatter);
	return formatter;
};

const integerDigitCount = (formatter: Intl.NumberFormat, value: number): number =>
	formatter
		.formatToParts(value)
		.filter((part) => part.type === "integer")
		.reduce((total, part) => total + part.value.length, 0);

export const formatLocaleCurrencyParts = (
	currency: string,
	language: AppLanguage = activeLanguage()
): Intl.NumberFormatPart[] =>
	localeNumberFormatter(language, {
		...narrowCurrencySymbolFor(currency),
		currency,
		minimumFractionDigits: 2,
		style: "currency"
	}).formatToParts(0);

const formatLocaleNumber = (
	value: number,
	options?: Intl.NumberFormatOptions,
	language: AppLanguage = activeLanguage()
): string => localeNumberFormatter(language, options).format(value);

export const formatLocaleCompact = (
	value: number,
	compactOptions: Intl.NumberFormatOptions,
	standardOptions: Intl.NumberFormatOptions,
	language: AppLanguage = activeLanguage()
): string => {
	const compact = localeNumberFormatter(language, compactOptions);
	if (integerDigitCount(compact, value) <= MAX_COMPACT_INTEGER_DIGITS) {
		return compact.format(value);
	}
	return localeNumberFormatter(language, standardOptions).format(value);
};

export default formatLocaleNumber;
